import Image from 'next/image'
import classNames from 'classnames'
import { useRouter } from 'next/router'

import { Container, Link, FacebookIcon, InstagramIcon, NewsletterIcon } from '~/components'

import * as styles from './Footer.css'

import vaduzLogo from '~/assets/vaduz-logo.svg'
import vpbankLogo from '~/assets/vpbank-logo.svg'
import { SettingsMenu } from '~/contentTypes/Settings/types'

type Props = React.ComponentPropsWithoutRef<'div'> & {
  menu: SettingsMenu
}

export function Footer({ className, menu, ...props }: Props) {
  const router = useRouter()

  return (
    <footer className={classNames([styles.footer, className])} {...props}>
      <Container>
        <div className={styles.main}>
          <div className={styles.partners}>
            <h2 className={styles.title}>Hauptpartner</h2>
            <div className={styles.partnersLogos}>
              <div className={styles.sponsor}>
                <a href="https://www.vpbank.com" target="_blank" rel="noreferrer">
                  <Image src={vpbankLogo} alt="VP Bank AG" width={196} height={64} className={styles.logoVPBank} />
                </a>
              </div>
              <div className={styles.sponsor}>
                <a href="https://www.vaduz.li" target="_blank" rel="noreferrer">
                  <Image src={vaduzLogo} alt="Gemeinde Vaduz" width={196} height={15} className={styles.logoVaduz} />
                </a>
              </div>
            </div>
          </div>

          <div className={styles.contact}>
            <h2 className={classNames([styles.title, styles.contactTitle])}>Veranstalter</h2>
            <strong>Verein Genussfestival Vaduz</strong>
            <br />
            c/o Skunk AG
            <br /> Wuhrstrasse 13, 9490 Vaduz
            <br /> <a href="tel:+423 231 18 28">T +423 231 18 28</a>
            <br />
            <a href="https://www.genussfestival.li">www.genussfestival.li</a>
          </div>

          <div className={styles.menu}>
            <ul className={styles.menuList}>
              {menu.map((item) => (
                <li key={item.id}>
                  <Link
                    href={item.href}
                    className={styles.menuLink}
                    data-active={item.href === '/' ? router.asPath === '/' : router.asPath.includes(item.href)}
                  >
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.share}>
            Bleib informiert:
            <ul className={styles.shareList}>
              <li>
                <Link href="/newsletter" className={styles.shareLink} aria-label="Newsletter">
                  <NewsletterIcon className={styles.shareIcon} />
                </Link>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/genussfestival_vaduz/"
                  className={styles.shareLink}
                  aria-label="Instagram"
                  target="_blank"
                  rel="noreferrer"
                >
                  <InstagramIcon className={styles.shareIcon} />
                </a>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/Genussfestival-Vaduz-105210368954470"
                  className={styles.shareLink}
                  aria-label="Facebook"
                  target="_blank"
                  rel="noreferrer"
                >
                  <FacebookIcon className={styles.shareIcon} />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </Container>
      <div className={styles.legal}>
        <div className={styles.legalMain}>
          <div className={styles.legalLeft}>
            © Genussfestival Vaduz <span className={styles.legalSpacer}>–</span> Alle Rechte vorbehalten.
          </div>
          <div className={styles.legalRight}>
            <ul className={styles.legalList}>
              <li>
                <Link
                  href="/agb"
                  className={classNames([styles.legalLink, router.asPath === '/agb' && styles.legalLinkActive])}
                >
                  AGB
                </Link>
              </li>
              <li>
                <Link
                  href="/impressum"
                  className={classNames([styles.legalLink, router.asPath === '/impressum' && styles.legalLinkActive])}
                >
                  Impressum
                </Link>
              </li>
              <li>
                <Link
                  href="/disclaimer"
                  className={classNames([styles.legalLink, router.asPath === '/disclaimer' && styles.legalLinkActive])}
                >
                  Disclaimer
                </Link>
              </li>
              <li>
                <Link
                  href="/datenschutz"
                  className={classNames([styles.legalLink, router.asPath === '/datenschutz' && styles.legalLinkActive])}
                >
                  Datenschutz
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  )
}
